import { useState } from "react";

import { useCan } from "components";
import { usePermanentRealTimeUser } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES, PIN_STATUSES, WARRANTY_TYPES } from "models";
import {
  useAddInterventionAttachmentMutation,
  useAddPinNotesAndAttachmentsMutation,
  useAppendPinMutation
} from "modules/AppointmentDetails/components/Interventions/hooks";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";

import type { DropdownItemProps } from "components";
import type { Intervention, PIN_TYPES } from "models";
import type { NoteAttachment } from "modules/AppointmentDetails/components/Interventions/components/NoteAttachments";
import type { PinCategoryFormValues } from "modules/AppointmentDetails/components/Interventions/components/PinCategory/components/PinCategoryForm";

type PinCategoryProps = {
  intervention: Intervention;
  selectedPinTypeOption: DropdownItemProps<PIN_TYPES>;
};

export const usePinCategory = ({ intervention, selectedPinTypeOption }: PinCategoryProps) => {
  const { user } = usePermanentRealTimeUser();
  const { mutateAsync: appendPin } = useAppendPinMutation();
  const { mutate: addInterventionAttachment } = useAddInterventionAttachmentMutation();
  const { mutateAsync: addPinNotesAndAttachments } = useAddPinNotesAndAttachmentsMutation();
  const selectedLocation = useAppointmentLocation();

  const [showPrintOverview, setShowPrintOverview] = useState(false);
  const [attachments, setAttachments] = useState<NoteAttachment[]>([]);

  const canManageInterventionAttachments = useCan(["create", "delete"], "intervention-attachments");

  const canAddFullPin = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.PinFull);
  const canAddPinNote = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.PinNote);

  const openModal = () => setShowPrintOverview(true);
  const closeModal = () => setShowPrintOverview(false);

  const handleSubmit = async (values: PinCategoryFormValues) => {
    const { claim_nr, support_nr, ref_nr, note, keep_parts, visible_important_items, pin_status_id, warranty_type_id, order_status } = values;

    const pin = {
      pin_type_id: selectedPinTypeOption.value,
      warranty_type_id: warranty_type_id ?? WARRANTY_TYPES.NotSet,
      pin_status_id: pin_status_id ?? PIN_STATUSES.NotSet,
      order_status: order_status || null,
      intervention_id: intervention.id,
      appointment_id: intervention.appointment_id,
      dealer_location_id: selectedLocation?.id ?? intervention.dealer_location_id,
      car_id: intervention.car_id ?? -1,
      user_id: user?.id ?? -1,
      claim_nr,
      ref_nr,
      support_nr,
      note,
      visible_important_items,
      keep_parts,
      recalls_id: "",
      pon_recall_id: "",
      rdw_recall_id: "",
      note_attachments: attachments.map(attach => attach.url)
    };

    if (canManageInterventionAttachments)
      attachments.forEach(attach =>
        addInterventionAttachment({ url: attach.url, name: attach.name, intervention_id: intervention.id, type: attach.type, visible_to_customer: true })
      );

    if (canAddFullPin) await appendPin(pin);
    if (canAddPinNote) await addPinNotesAndAttachments(pin);
  };

  return {
    openModal,
    closeModal,
    handleSubmit,
    setAttachments,
    attachments,
    showPrintOverview
  };
};
